import React, { useState, useEffect, useCallback } from 'react';
// import BackToServicesButton from '../components/BackToServicesButton';
import { authFetch } from '../authFetch';
import { useAuth } from '../authContext';
import useSelectedRadio from '../hooks/useSelectedRadio';
import API_BASE_URL from '../api';
import { useTranslation } from 'react-i18next';
import { fmtNumber, useLocale } from '../i18n/format';
import { resolveCountryFlag } from '../constants/countries';
import { buildAdsDoc } from "../pdf/builders/ads/buildDoc";
import ExportButton from '../components/ExportButton';
import { buildAdsWorkbook } from "../excel/builders/ads/buildWorkbook";

export default function AdsServicePage() {
  const [date, setDate] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState('');
  const { isAuth } = useAuth();
  const { radioId, shortcode, name: stationName } = useSelectedRadio(); // шорткод + имя станции
  const { t } = useTranslation('common');
  const locale = useLocale();

  // === GEO/IP модалки ===
  const [geoModal, setGeoModal] = useState({ open: false, block: null, items: [], loading: false, error: '' });
  const [ipModal, setIpModal]   = useState({ open: false, country: null, country_code: null, items: [], loading: false, error: '' });
  const [hoveredGeoRow, setHoveredGeoRow] = useState(null);

  const openGeoModal = async (block) => {
    if (!radioId || !block?.start || !block?.end) return;
    const API = (API_BASE_URL || '').replace(/\/+$/, '');
    setHoveredGeoRow(null);
    setGeoModal({ open: true, block, items: [], loading: true, error: '' });
    try {
      const url = new URL(`${API}/radios/${encodeURIComponent(radioId)}/metrics/ads/listeners-geo`);
      url.searchParams.set('start_time', `${date} ${block.start.slice(0,8)}`);
      url.searchParams.set('end_time',   `${date} ${block.end.slice(0,8)}`);
      url.searchParams.set('limit', '10');
      const res = await authFetch(url.toString());
      const data = await res.json();
      const items = Array.isArray(data.items) ? data.items : [];
      setGeoModal((m) => ({ ...m, items, loading: false }));
    } catch (e) {
      setGeoModal((m) => ({ ...m, loading: false, error: t('network_error') }));
    }
  };

  const openIpModal = async (country, country_code) => {
    if (!radioId || !geoModal.block) return;
    const { block } = geoModal;
    const API = (API_BASE_URL || '').replace(/\/+$/, '');
    setIpModal({ open: true, country, country_code, items: [], loading: true, error: '' });
    try {
      const url = new URL(`${API}/radios/${encodeURIComponent(radioId)}/metrics/ads/listeners-ips`);
      url.searchParams.set('start_time', `${date} ${block.start.slice(0,8)}`);
      url.searchParams.set('end_time',   `${date} ${block.end.slice(0,8)}`);
      if (country_code) url.searchParams.set('country_code', country_code); // если кода нет — покажем все IP
      else url.searchParams.set('country', country); // если нет ISO-кода — фильтруем по названию страны
      url.searchParams.set('limit', '10000');
      const res = await authFetch(url.toString());
      const data = await res.json();
      const items = Array.isArray(data.items) ? data.items : [];
      setIpModal((m) => ({ ...m, items, loading: false }));
    } catch (e) {
      setIpModal((m) => ({ ...m, loading: false, error: t('network_error') }));
    }
  };

  const closeGeoModal = () => {
    setHoveredGeoRow(null);
    setGeoModal({ open: false, block: null, items: [], loading: false, error: '' });
  };
  const closeIpModal  = () => setIpModal({ open: false, country: null, country_code: null, items: [], loading: false, error: '' });

  const countryFlag = (code, name) => {
    const flag = resolveCountryFlag(code || name || '');
    return flag || '🏳️';
  };

  // дефолтная дата — сегодня
  useEffect(() => {
    if (!date) {
      const today = new Date();
      setDate(today.toISOString().slice(0, 10));
    }
  }, [date]);

  const fetchBlocks = useCallback(async () => {
    if (!isAuth || !radioId || !date) return;
    setLoading(true);
    setInfo('');
    setBlocks([]);

    try {
      const API = (API_BASE_URL || '').replace(/\/+$/, '');

      // 1) Расписание рекламных блоков
      const scheduleUrl = `${API}/radios/${encodeURIComponent(radioId)}/metrics/ads/schedule?date=${encodeURIComponent(date)}`;
      const res = await authFetch(scheduleUrl);
      const data = await res.json();

      if (!Array.isArray(data.data) || !data.data.length) {
        setInfo(t('ads.no_blocks'));
        setBlocks([]);
        setLoading(false);
        return;
      }

      // Оставляем только блоки (J — начало, C — ролик) за выбранную дату
      const schedule = data.data.filter(
        row =>
          row.blocktype === 'Р' &&
          (row.elemclass === 'J' || row.elemclass === 'C') &&
          row.playlistdate === date
      );

      let blocksArr = [];
      let current = null;

      schedule.forEach(row => {
        if (row.elemclass === 'J') {
          if (current && current.clips.length) blocksArr.push(current);
          current = { start: row.elemstarttime, end: '', clips: [] };
        }
        if (row.elemclass === 'C') {
          if (!current) current = { start: row.elemstarttime, end: '', clips: [] };
          current.clips.push(row.elemname);
          current.end = row.elemendtime;
        }
      });
      if (current && current.clips.length) blocksArr.push(current);

      // 2) Для каждого блока считаем слушателей
      const fetchCounts = async (block) => {
        let count = 0;
        if (block.start && block.end) {
          const endDate = `${date} ${block.end.slice(0, 8)}`;
          const dt = new Date(`${endDate.replace(/-/g, '/')} UTC+3`);
          // считаем только прошедшие блоки
          if (dt.getTime() <= Date.now()) {
            const countUrl =
              `${API}/radios/${encodeURIComponent(radioId)}/metrics/ads/listeners-count` +
              `?start_time=${encodeURIComponent(`${date} ${block.start.slice(0, 8)}`)}` +
              `&end_time=${encodeURIComponent(`${date} ${block.end.slice(0, 8)}`)}`;

            try {
              const resp = await authFetch(countUrl);
              const cnt = await resp.json();
              if (cnt && typeof cnt.listeners !== 'undefined') count = cnt.listeners;
            } catch {}
          }
        }
        return { ...block, listeners: count };
      };

      const blocksWithCounts = await Promise.all(blocksArr.map(fetchCounts));
      setBlocks(blocksWithCounts);
      if (!blocksWithCounts.length) setInfo(t('ads.no_blocks'));
    } catch {
      setInfo(t('network_error'));
      setBlocks([]);
    }

    setLoading(false);
  }, [date, isAuth, radioId, t]);

  const clean = (value) => String(value ?? '').replace(/[^0-9A-Za-z_-]/g, '-');
  const filenameBase = `ads_${clean(shortcode || 'channel')}_${clean(date || '')}`;
  const exportDisabled = !blocks.length || loading;
  const exportButtonStyle = buildPrimaryButtonStyle(exportDisabled);

  // автоподгрузку отключили, данные получаем по кнопке

  // если нет выбранного канала — показываем понятное сообщение
  if (!radioId) {
    return (
      <div style={widgetStyle}>
        {/* BackToServicesButton removed */}
        <h2 style={{ marginBottom: 8 }}>{t('ads.title')}</h2>
        <div style={{ color: '#e54848', fontWeight: 600, fontSize: 18 }}>
          {t('no_channel')}
        </div>
      </div>
    );
  }

  return (
    <div style={widgetStyle}>
      {/* BackToServicesButton removed */}

      {/* Текстове поле-опис */}
      <div
        style={{
          width: "100%",
          padding: "8px 12px",
          borderRadius: 8,
          background: "#f8f8f8",
      color: "#1a1a1a",
      fontSize: 17,
      margin: "8px 0 18px 0",
      boxSizing: "border-box"
    }}
  >
        {t('ads.description')}
  </div>

      <h2 style={{ marginBottom: 8 }}>{t('ads.title')}</h2>
      <div style={{ marginBottom: 16, display: 'flex', gap: ACTION_BUTTON_GAP, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 500 }}>📅 {t('ads.date')}:</span>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          style={inputStyle}
        />
        <button onClick={fetchBlocks} style={showBtn}>{t('show')}</button>
        <ExportButton
          data={blocks}
          onBeforeBuild={async () => {
            const url = `${process.env.PUBLIC_URL || ''}/Radiomonitor.NetLogo.png`;
            try {
              const resp = await fetch(url, { cache: 'no-store' });
              const blob = await resp.blob();
              const reader = new FileReader();
              const dataUrl = await new Promise((res, rej) => {
                reader.onloadend = () => res(reader.result);
                reader.onerror = rej;
                reader.readAsDataURL(blob);
              });
              return { logoDataUrl: dataUrl };
            } catch {
              return { logoDataUrl: null };
            }
          }}
          buildDoc={(payload) =>
            buildAdsDoc({
              t, shortcode, stationName,
              serviceName: t('ads.title'),
              date, blocks, fmtNumber, locale,
              logoDataUrl: payload?.logoDataUrl || null
            })
          }
          buildWorkbook={(XLSX) =>
            buildAdsWorkbook(XLSX, {
              t, shortcode, stationName,
              serviceName: t('ads.title'),
              date, blocks, fmtNumber, locale
            })
          }
          filename={`${filenameBase}.pdf`}
          xlsxFilename={`${filenameBase}.xlsx`}
          label={t('export_pdf')}
          labelXlsx={t('export_excel')}
          style={exportButtonStyle}
          disabled={exportDisabled}
        />
      </div>

      {loading && <div style={{ margin: "18px 0", fontWeight: 500 }}>{t('loading')}</div>}
      {info && !loading && <div style={{ margin: "18px 0", fontWeight: 500 }}>{info}</div>}

      {!loading && blocks.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 16 }}>
            <thead>
              <tr style={{ background: "#f0f0f0" }}>
                <th style={thTd}>{t('ads.table.start')}</th>
                <th style={thTd}>{t('ads.table.end')}</th>
                <th style={thTd}>{t('ads.table.clips')}</th>
                <th style={thTd}>{t('ads.table.listeners')}</th>
                <th style={thTd}>{t('ads.table.details') || 'Докладніше'}</th>
              </tr>
            </thead>
            <tbody>
              {blocks.map((block, idx) => (
                <tr key={idx}>
                  <td style={thTd}>{block.start?.slice(0, 8)}</td>
                  <td style={thTd}>{block.end?.slice(0, 8)}</td>
                  <td style={thTd}>
                    {block.clips.map((clip, j) => (
                      <span key={j}>{clip}<br /></span>
                    ))}
                  </td>
                  <td style={{ ...thTd, textAlign: "center" }}>
                    {fmtNumber(block.listeners || 0, locale)}
                  </td>
                  <td style={{ ...thTd, textAlign: "center" }}>
                    <button
                      onClick={() => openGeoModal(block)}
                      style={{ ...showBtn, minWidth: 120, padding: '6px 12px', background: '#0ea5e9' }}
                      disabled={!block.start || !block.end}
                    >
                      {t('ads.table.details') || 'Докладніше'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* === GEO MODAL === */}
      {geoModal.open && (
        <div style={modalOverlay}>
          <div style={modalCard}>
            <div style={modalHeader}>
              <div style={{fontWeight:700,fontSize:18}}>
                {t('ads.geo_title') || 'Кількість слухачів по країнах'} — {geoModal.block?.start?.slice(0,8)}–{geoModal.block?.end?.slice(0,8)}
              </div>
              <button onClick={closeGeoModal} style={modalClose}>×</button>
            </div>

            {geoModal.loading && <div style={{padding:12}}>{t('loading')}</div>}
            {geoModal.error && <div style={{padding:12, color:'#e54848'}}>{geoModal.error}</div>}

            {!geoModal.loading && !geoModal.error && (
              <div style={{maxHeight: '60vh', overflowY: 'auto', padding: 12}}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 16 }}>
                  <thead>
                    <tr style={{ background: "#f0f0f0" }}>
                      <th style={thTd}>{t('ads.geo_modal.country') || 'Країна'}</th>
                      <th style={{...thTd, textAlign:'center'}}>{t('ads.table.listeners') || 'Слушатели'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const items = geoModal.items || [];
                      const topSum = items.reduce((s, it) => s + (it.listeners||0), 0);
                      const total = Number(geoModal.block?.listeners || 0);
                      const rest  = Math.max(total - topSum, 0);
                      return (
                        <>
                          {items.map((it, i) => (
                            <tr
                              key={i}
                              style={{
                                cursor:'pointer',
                                background: hoveredGeoRow === i ? '#dbeafe' : 'transparent'
                              }}
                              onClick={() => openIpModal(it.country || 'Без гео', it.country_code || '')}
                              onMouseEnter={() => setHoveredGeoRow(i)}
                              onMouseLeave={() => setHoveredGeoRow(null)}
                            >
                              <td style={thTd}>
                                <span style={{marginRight:8, fontSize:20}} aria-hidden="true">{countryFlag(it.country_code, it.country)}</span>
                                {it.country || (t('ads.geo_modal.no_geo') || 'Без гео')}
                              </td>
                              <td style={{...thTd, textAlign:'center'}}>{fmtNumber(it.listeners||0, locale)}</td>
                            </tr>
                          ))}
                          <tr>
                            <td style={{...thTd, fontWeight:600}}>
                              <span style={{marginRight:8, fontSize:20}} aria-hidden="true">🌍</span>
                              {t('ads.geo_modal.others') || 'Інші'}
                            </td>
                            <td style={{...thTd, textAlign:'center', fontWeight:600}}>{fmtNumber(rest, locale)}</td>
                          </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* === IPs MODAL === */}
      {ipModal.open && (
        <div style={modalOverlay}>
          <div style={modalCard}>
            <div style={modalHeader}>
              <div style={{fontWeight:700,fontSize:18}}>
                {t('ads.ips_modal.title') || 'IP слухачів'} — {ipModal.country || (t('no_geo') || 'Без гео')}
              </div>
              <button onClick={closeIpModal} style={modalClose}>×</button>
            </div>

            {ipModal.loading && <div style={{padding:12}}>{t('loading')}</div>}
            {ipModal.error && <div style={{padding:12, color:'#e54848'}}>{ipModal.error}</div>}

            {!ipModal.loading && !ipModal.error && (
              <div style={{maxHeight: '60vh', overflowY: 'auto', padding: 12}}>
                {ipModal.items.length === 0 ? (
                  <div style={{padding:12, opacity:0.8}}>{t('no_data') || 'Нет данных'}</div>
                ) : (
                  <ul style={{margin:0, padding:'0 0 0 18px'}}>
                    {ipModal.items.map((ip, i) => (<li key={i} style={{lineHeight:'28px'}}>{ip}</li>))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const widgetStyle = {
  maxWidth: 900,
  margin: '40px auto',
  background: '#fff',
  borderRadius: 14,
  padding: 24,
  boxShadow: '0 2px 12px 0 #0001'
};

const inputStyle = {
  marginRight: 8,
  padding: 7,
  borderRadius: 4,
  border: '1px solid #ccc',
  fontSize: 16,
  minWidth: 130
};

const ACTION_BUTTON_WIDTH = 170;
const ACTION_BUTTON_GAP = 12;

const primaryButtonBase = {
  border: 'none',
  padding: '8px 16px',
  borderRadius: 5,
  fontWeight: 600,
  minWidth: ACTION_BUTTON_WIDTH,
  textAlign: 'center'
};

function buildPrimaryButtonStyle(disabled = false) {
  return {
    ...primaryButtonBase,
    background: disabled ? '#9bbdfd' : '#3867fa',
    color: '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer'
  };
}

const showBtn = buildPrimaryButtonStyle(false);

const thTd = {
  padding: "7px 12px",
  border: "1px solid #e0e0e0",
  textAlign: "left",
  verticalAlign: "top"
};

const modalOverlay = {
  position:'fixed', inset:0, background:'rgba(0,0,0,0.45)',
  display:'flex', alignItems:'center', justifyContent:'center', zIndex: 9999
};
const modalCard = {
  width:'min(760px, 92vw)', background:'#fff', borderRadius:12,
  boxShadow:'0 10px 40px rgba(0,0,0,0.2)', overflow:'hidden',
  border:'1px solid rgba(0,110,69,0.25)'
};
const modalHeader = {
  display:'flex', alignItems:'center', justifyContent:'space-between',
  padding:'14px 18px', borderBottom:'none',
  background:'linear-gradient(90deg, #006e45 0%, #009f5f 100%)',
  color:'#fff', boxShadow:'0 3px 8px rgba(0,0,0,0.12)'
};
const modalClose = {
  background:'transparent', border:'none', fontSize:24, lineHeight:1,
  cursor:'pointer', padding:'0 6px', color:'#fff'
};
